import { Controller } from '@hotwired/stimulus';

/**
 * ComparisonTableController
 *
 * Manages the mobile quote comparison experience with two key features:
 * - Sticky Header Visibility: Shows/hides sticky cards based on scroll position
 * - Synchronized Touch Scrolling: Enables card-by-card swiping across multiple containers
 */
export default class extends Controller {
  static targets = [
    'stickyHead', // Fixed header container
    'tableHead', // Table header (intersection observer target)
    'stickyContainer', // Sticky scrollable content
    'stickyWrapper', // Sticky viewport wrapper
    'tableContainer', // Table scrollable content
    'tableWrapper', // Table viewport wrapper
    'scrollLeftBtn', // Left navigation button
    'scrollRightBtn', // Right navigation button
  ];

  static values = {
    quoteColumns: {type: Number, default: 1},
  };

  // Configuration constants
  static STICKY_HEADER_THRESHOLD = 20; // px from top before showing sticky
  static BOTTOM_BUFFER = 350; // px buffer before hiding at bottom
  static DIRECTION_THRESHOLD = 5; // px movement to determine scroll direction
  static SNAP_TRANSITION = 'margin-left 0.3s cubic-bezier(0, 0.5, 1, 1)';
  static DEFAULT_CARD_WIDTH = 180;
  static DEFAULT_GAP = 10;

  connect() {
    this.applyQuoteColumns();
    this.initializeCardDimensions();
    this.initializeSyncState();
    this.initializeStickyHeaderVisibility();
    this.initializeTouchScrolling();
    this.initializeNavButtons();
  }

  disconnect() {
    this.cleanup();
  }

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  // Set via CSSOM rather than an inline style attribute, which style-src governs.
  applyQuoteColumns() {
    this.element.style.setProperty('--quote-columns', this.quoteColumnsValue);
  }

  initializeCardDimensions() {
    const contentRow = this.stickyContainerTarget.querySelector('.sticky-quotes-row');
    if (!contentRow) return;

    const computedStyle = window.getComputedStyle(contentRow);
    const gap = parseFloat(computedStyle.columnGap) || this.constructor.DEFAULT_GAP;

    const firstCard = contentRow.querySelector('.scrollable-quote-card-wrapper');
    const cardWidth = firstCard?.offsetWidth ||
                      parseFloat(computedStyle.gridAutoColumns) ||
                      this.constructor.DEFAULT_CARD_WIDTH;

    this.cardWidth = cardWidth;
    this.cardGap = gap;
    this.cardTotalWidth = cardWidth + gap; // Used for snap calculations

    if (this.quoteColumnsValue > 1 && !this.element.style.getPropertyValue('--card-width')) {
      // Lock --card-width on first connect so it doesn't recompute on orientation change
      this.element.style.setProperty('--card-width', `${cardWidth}px`);
    }
  }

  initializeSyncState() {
    this.syncedContainers = []; // Track all containers for synchronization
    this.eventListeners = []; // Track for cleanup
    this.cachedStickyHeight = null; // Performance optimization
  }

  initializeTouchScrolling() {
    // Set up synchronized drag scrolling for both containers
    this.setupDragScroll(this.stickyContainerTarget, this.stickyWrapperTarget, true);
    this.setupDragScroll(this.tableContainerTarget, this.tableWrapperTarget, true);
  }

  initializeNavButtons() {
    if (!this.hasScrollLeftBtnTarget || !this.hasScrollRightBtnTarget) return;

    // Find first and last cards using their marker classes
    const tableContainer = this.tableContainerTarget;
    this.firstCard = tableContainer.querySelector('.quote-card--first');
    this.lastCard = tableContainer.querySelector('.quote-card--last');

    if (!this.firstCard || !this.lastCard) return;

    // Set up IntersectionObserver to watch card visibility within the wrapper
    this.navButtonObserver = new IntersectionObserver(
      (entries) => this.updateNavButtonVisibility(entries),
      {
        root: this.tableWrapperTarget,
        threshold: 0.9, // Card is considered "visible" when 90% is in view
      },
    );

    this.navButtonObserver.observe(this.firstCard);
    this.navButtonObserver.observe(this.lastCard);
  }

  updateNavButtonVisibility(entries) {
    entries.forEach((entry) => {
      const isFirst = entry.target.classList.contains('quote-card--first');
      const isLast = entry.target.classList.contains('quote-card--last');

      if (isFirst) {
        // Hide left button when first card is visible
        if (entry.isIntersecting) {
          this.scrollLeftBtnTarget.classList.add('scroll-nav-btn--hidden');
        } else {
          this.scrollLeftBtnTarget.classList.remove('scroll-nav-btn--hidden');
        }
      }

      if (isLast) {
        // Hide right button when last card is visible
        if (entry.isIntersecting) {
          this.scrollRightBtnTarget.classList.add('scroll-nav-btn--hidden');
        } else {
          this.scrollRightBtnTarget.classList.remove('scroll-nav-btn--hidden');
        }
      }
    });
  }

  // ============================================================================
  // NAVIGATION BUTTON HANDLERS
  // ============================================================================

  scrollLeft() {
    const currentMargin = parseInt(this.tableContainerTarget.style.marginLeft) || 0;
    this.applyNavScroll(currentMargin + this.cardTotalWidth);
  }

  scrollRight() {
    const currentMargin = parseInt(this.tableContainerTarget.style.marginLeft) || 0;
    this.applyNavScroll(currentMargin - this.cardTotalWidth);
  }

  // Apply a nav scroll by letting the table container clamp the value, then mirror that exact
  // result to all other containers — preventing misalignment when wrapper widths differ
  // (e.g. sticky wrapper after orientation change)
  applyNavScroll(targetMargin) {
    const tableInstance = this.syncedContainers.find(i => i.container === this.tableContainerTarget);
    if (!tableInstance) return;

    tableInstance.container.style.transition = this.constructor.SNAP_TRANSITION;
    tableInstance.setMargin(targetMargin, true);

    const appliedMargin = parseInt(this.tableContainerTarget.style.marginLeft) || 0;

    this.syncedContainers.forEach(instance => {
      if (instance.container === this.tableContainerTarget) return;
      instance.container.style.transition = this.constructor.SNAP_TRANSITION;
      instance.container.style.marginLeft = `${appliedMargin}px`;
    });
  }

  // ============================================================================
  // STICKY HEADER VISIBILITY LOGIC
  // ============================================================================

  /**
   * Controls when the sticky header appears/disappears
   *
   * Strategy:
   * - Use IntersectionObserver to detect when table header scrolls out of view
   * - When out of view, enable scroll-based bottom boundary checking
   * - Hide when approaching table bottom to avoid overlap
   *
   */
  initializeStickyHeaderVisibility() {
    if (!this.hasTableHeadTarget) return;

    this.scrollContainer = this.element.closest('#wrap[data-new-layout="true"]') || window;

    const options = {
      rootMargin: `-${this.constructor.STICKY_HEADER_THRESHOLD}px 0px 0px 0px`,
      threshold: [0, 1]
    };

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          // Table header scrolled out -> enable bottom checking
          this.enableScrollBasedBottomCheck();
        } else {
          // Table header visible -> hide sticky and disable checking
          this.disableScrollBasedBottomCheck();
          this.hideStickyHeader();
        }
      });
    }, options);

    this.intersectionObserver.observe(this.tableHeadTarget);
  }

  enableScrollBasedBottomCheck() {
    if (!this.throttledScrollHandler) {
      this.throttledScrollHandler = this.createThrottledScrollHandler();
    }
    this.scrollContainer.addEventListener('scroll', this.throttledScrollHandler, { passive: true });
    this.updateStickyHeaderWithBottomCheck();
  }

  disableScrollBasedBottomCheck() {
    if (this.throttledScrollHandler && this.scrollContainer) {
      this.scrollContainer.removeEventListener('scroll', this.throttledScrollHandler);
    }
  }

  createThrottledScrollHandler() {
    let ticking = false;
    return () => {
      if (!ticking) {
        this.scrollAnimationFrame = requestAnimationFrame(() => {
          this.updateStickyHeaderWithBottomCheck();
          ticking = false;
        });
        ticking = true;
      }
    };
  }

  /**
   * Determines if sticky header should be visible based on table position
   * Also controls nav button visibility - hide when scrolled past comparison table
   */
  updateStickyHeaderWithBottomCheck() {
    if (!this.hasStickyHeadTarget) return;

    // Cache height for performance (sticky height doesn't change)
    if (this.cachedStickyHeight === null) {
      const stickyContent = this.stickyHeadTarget.querySelector('.sticky-quotes-wrapper');
      if (stickyContent) {
        this.cachedStickyHeight = stickyContent.offsetHeight;
      } else {
        this.showStickyHeader();
        return;
      }
    }

    const tableRect = this.element.getBoundingClientRect();
    const shouldShow = tableRect.bottom > (this.cachedStickyHeight + this.constructor.BOTTOM_BUFFER);

    if (shouldShow) {
      this.showStickyHeader();
      this.showNavButtons();
    } else {
      this.hideStickyHeader();
      this.hideNavButtons();
    }
  }

  showStickyHeader() {
    if (!this.hasStickyHeadTarget) return;
    Object.assign(this.stickyHeadTarget.style, {
      opacity: '1',
      visibility: 'visible',
      pointerEvents: 'auto'
    });
  }

  hideStickyHeader() {
    if (!this.hasStickyHeadTarget) return;
    Object.assign(this.stickyHeadTarget.style, {
      opacity: '0',
      visibility: 'hidden',
      pointerEvents: 'none'
    });
  }

  showNavButtons() {
    if (this.hasScrollLeftBtnTarget) {
      this.scrollLeftBtnTarget.classList.remove('scroll-nav-btn--out-of-view');
    }
    if (this.hasScrollRightBtnTarget) {
      this.scrollRightBtnTarget.classList.remove('scroll-nav-btn--out-of-view');
    }
  }

  hideNavButtons() {
    if (this.hasScrollLeftBtnTarget) {
      this.scrollLeftBtnTarget.classList.add('scroll-nav-btn--out-of-view');
    }
    if (this.hasScrollRightBtnTarget) {
      this.scrollRightBtnTarget.classList.add('scroll-nav-btn--out-of-view');
    }
  }

  // ============================================================================
  // TOUCH SCROLLING & SYNCHRONIZATION
  // ============================================================================

  /**
   * Sets up touch-based horizontal scrolling with snap behavior
   *
   * Key Features:
   * - Direction locking: Commit to horizontal OR vertical, not both
   * - Snap-to-card: Always lands centered on a card
   * - Synchronized scrolling: Multiple containers move together
   *
   */
  setupDragScroll(container, wrapper, syncWithOthers = false) {
    if (!container || !wrapper) return;

    const contentRow = container.querySelector('[data-cards-row]');
    if (!contentRow) return;

    const cards = contentRow.querySelectorAll('.scrollable-quote-card-wrapper');
    if (cards.length === 0) return;

    const state = this.createDragState();
    const handlers = this.createDragHandlers(container, wrapper, cards, state, syncWithOthers);

    // Register touch events
    this.addTrackedListener(container, 'touchstart', handlers.handleStart, { passive: true });
    this.addTrackedListener(document, 'touchmove', handlers.handleMove, { passive: false });
    this.addTrackedListener(document, 'touchend', handlers.handleEnd);
    this.addTrackedListener(container, 'contextmenu', (e) => e.preventDefault());

    // Register for synchronization
    if (syncWithOthers) {
      this.syncedContainers.push({ container, wrapper, setMargin: handlers.setMargin });
    }
  }

  createDragState() {
    return {
      isDragging: false,
      startX: 0,
      startY: 0,
      currentX: 0,
      currentY: 0,
      initialMargin: 0,
      currentMargin: 0,
      currentCardIndex: 0,
      scrollDirection: null, // 'horizontal' | 'vertical' | null
      directionLocked: false
    };
  }

  createDragHandlers(container, wrapper, cards, state, syncWithOthers) {
    const getCurrentMargin = () => {
      const style = container.style.marginLeft;
      return style ? parseInt(style) : 0;
    };

    /**
     * Sets margin with boundary constraints and optional synchronization
     *
     * - When one container scrolls, broadcast to all others
     * - Constrain between 0 (start) and maxScroll (end)
     */
    const setMargin = (value, skipSync = false) => {
      const maxScroll = this.calculateMaxScroll(container, wrapper);
      const constrainedValue = Math.max(maxScroll, Math.min(0, value));

      container.style.marginLeft = `${constrainedValue}px`;
      state.currentMargin = constrainedValue;

      // Broadcast to other containers (avoid infinite loops with skipSync)
      if (syncWithOthers && !skipSync) {
        this.syncMarginToOthers(container, constrainedValue);
      }
    };

    // ===== SNAP BEHAVIOR =====

    /**
     * Calculates the nearest card position to snap to
     *
     * - Convert current scroll offset to card index (with rounding)
     * - Constrain index to valid range
     * - Convert back to pixel position
     */
    const calculateSnapPosition = (currentMargin) => {
      const currentOffset = Math.abs(currentMargin);
      const cardIndex = Math.round(currentOffset / this.cardTotalWidth);
      const constrainedIndex = Math.max(0, Math.min(cardIndex, cards.length - 1));
      const snapPosition = -1 * (constrainedIndex * this.cardTotalWidth);

      return { snapPosition, cardIndex: constrainedIndex };
    };

    const setTransition = (enable) => {
      const transition = enable ? this.constructor.SNAP_TRANSITION : 'none';
      container.style.transition = transition;

      if (syncWithOthers) {
        this.syncedContainers.forEach(instance => {
          instance.container.style.transition = transition;
        });
      }
    };

    // ===== TOUCH EVENT HANDLERS =====

    const handleStart = (e) => {
      state.isDragging = true;
      state.directionLocked = false;
      state.scrollDirection = null;
      setTransition(false); // Disable for immediate response
      state.startX = e.touches[0].clientX;
      state.startY = e.touches[0].clientY;
      state.initialMargin = getCurrentMargin();
    };

    /**
     * - Handle touch movement with directional locking
     * - Wait for minimum movement (reduces false positives)
     * - Compare horizontal vs vertical delta
     * - Lock to dominant direction
     * - If horizontal: prevent default, handle scroll
     * - If vertical: allow native scroll, cancel drag
     */
    const handleMove = (e) => {
      if (!state.isDragging) return;

      state.currentX = e.touches[0].clientX;
      state.currentY = e.touches[0].clientY;

      // Determine direction on first significant movement
      if (!state.directionLocked) {
        const deltaX = Math.abs(state.currentX - state.startX);
        const deltaY = Math.abs(state.currentY - state.startY);

        if (
          deltaX > this.constructor.DIRECTION_THRESHOLD ||
          deltaY > this.constructor.DIRECTION_THRESHOLD
        ) {
          state.scrollDirection = deltaX > deltaY ? 'horizontal' : 'vertical';
          state.directionLocked = true;
        }
      }

      // Execute based on locked direction
      if (state.scrollDirection === 'horizontal') {
        e.preventDefault(); // Block vertical scroll
        const deltaX = state.currentX - state.startX;
        setMargin(state.initialMargin + deltaX);
      } else if (state.scrollDirection === 'vertical') {
        // Allow native scroll, exit drag mode
        state.isDragging = false;
        setTransition(true);
      }
    };

    const handleEnd = () => {
      if (!state.isDragging) return;

      state.isDragging = false;
      const currentMargin = getCurrentMargin();
      const { snapPosition, cardIndex } = calculateSnapPosition(currentMargin);

      state.currentCardIndex = cardIndex;
      setTransition(true); // Enable for smooth snap animation
      setMargin(snapPosition);
    };

    return { handleStart, handleMove, handleEnd, setMargin };
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  calculateMaxScroll(container, wrapper) {
    return -(container.scrollWidth - wrapper.offsetWidth);
  }

  syncMarginToOthers(sourceContainer, value) {
    this.syncedContainers.forEach((instance) => {
      if (instance.container !== sourceContainer && instance.setMargin) {
        instance.setMargin(value, true); // skipSync=true to avoid loop
      }
    });
  }

  addTrackedListener(element, event, handler, options = {}) {
    element.addEventListener(event, handler, options);
    this.eventListeners.push({ element, event, handler, options });
  }

  cleanup() {
    // Cancel animation frames
    if (this.scrollAnimationFrame) {
      cancelAnimationFrame(this.scrollAnimationFrame);
    }

    // Disconnect observers
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }

    if (this.navButtonObserver) {
      this.navButtonObserver.disconnect();
      this.navButtonObserver = null;
    }

    // Remove scroll listener
    this.disableScrollBasedBottomCheck();

    // Remove all event listeners
    this.eventListeners.forEach(({ element, event, handler, options }) => {
      element.removeEventListener(event, handler, options);
    });

    // Clear references
    this.eventListeners = [];
    this.syncedContainers = [];
    this.cachedStickyHeight = null;
  }
}
